import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { CalendarView, DatePickerComponent, DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { ClientDTO, PagedResultDTOOfParkerDTO } from '../../../../../base/models/models.swagger.generated';
import { ClientService } from '../../../../../base/services/client/client.service';
import { Observable, of } from 'rxjs';
import { AutocompleteItem } from '../../../../../base/shared/autocomplete/autocomplete-item';
import { map } from 'rxjs/operators';
import { ParkerExtractReportService } from '../parker-extract-report.service';
import { AuthService } from '../../../../../base/services/auth.service';
import { ParkerExtractFormType } from './parker-extract-form.type';
import { TranslateModule } from '@ngx-translate/core';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AutocompleteComponent } from '../../../../../base/shared/autocomplete/autocomplete.component';
import { ClientAutocompleteComponent } from '../../../../../base/shared/client-autocomplete/client-autocomplete.component';
import { NgIf } from '@angular/common';

@Component({
    selector: 'app-parker-extract-form',
    templateUrl: './parker-extract-form.component.html',
    styleUrls: ['./parker-extract-form.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        NgIf,
        ClientAutocompleteComponent,
        DatePickerModule,
        AutocompleteComponent,
        DropDownListModule,
        TranslateModule,
    ],
})
export class ParkerExtractFormComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(ClientAutocompleteComponent)
  public clientAutocomplete: ClientAutocompleteComponent;

  @ViewChild('monthPicker')
  public monthPicker: DatePickerComponent;

  @Input() public form: FormGroup<ParkerExtractFormType>;

  @Output()
  public searchDTOChanged = new EventEmitter<any>();

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public set selectedClient(value: ClientDTO) {
    if (this.clientService.selectedClient != value) {
      this.clientService.selectedClient = value;
    }
    // Defer the event emission to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.onClientSelected(value);
    });
  }

  public get selectedParkerId(): number {
    return this.form.controls.parkerId.value;
  }

  public set selectedParkerId(value: number) {
    //Check if actual record has been selected
    if (typeof value === 'number') {
      this.form.controls.parkerId.setValue(value);
    } else {
      this.form.controls.parkerId.setValue(null);
    }
  }

  public get allowClientSelection(): boolean {
    return this.authService.isUserAdminOrClientAdmin() || this.authService.hasUserClientAssigned();
  }

  public keyValueDropdownSettings = {text: 'name', value: 'id'};
  public calendarDepth: CalendarView = 'Year';
  public calendarStart: CalendarView = 'Year';

  public subscriptions = [];
  public clientParkerDropdownValues: {name: string, id: number}[] = [];

  constructor(public authService: AuthService,
              protected clientService: ClientService,
              private parkerExtractReportService: ParkerExtractReportService) {
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {

        this.searchDTOChanged.emit();
      }));

    if (this.authService.isUserStandardUser()) {
      this.clientParkerDropdownValues = this.authService.getUserInfo().linkedParkers.filter(p => p.isClientManaged).map(p => {
        return {id: p.parkerId, name: p.client?.name};
      }).sort((a, b) => a.name.localeCompare(b.name));
      if (this.clientParkerDropdownValues.length === 1) {
        this.form.controls.parkerId.setValue(this.clientParkerDropdownValues[0].id);
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.selectedClient) {
      this.focusClientAutocomplete();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public onClientSelected(client: ClientDTO): void {
    if (client) {
      this.searchDTOChanged.emit();
    }
  }

  public searchParkerDropdownValues(searchText: string): Observable<AutocompleteItem[]> {
    if (this.authService.isUserStandardUser()) {
      return of([]);
    }

    return this.parkerExtractReportService.searchParkerDropdownValues(searchText)
      .pipe(map((parkerDropdownValues: PagedResultDTOOfParkerDTO) => {
        const data: AutocompleteItem[] = [];
        parkerDropdownValues.items.forEach(customer => {
          const fullName = customer.firstName + ' ' + customer.lastName;
          data.push({id: customer.id, name: fullName});
        });
        return data;
      }));
  }

  private focusClientAutocomplete(): void {
    if (this.clientAutocomplete) {
      this.clientAutocomplete.focus();
    }
  }
}
